import React from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { alpha, styled } from '@mui/material/styles';
import MuiAppBar from '@mui/material/AppBar';
import { Box, Button, IconButton, Stack, Toolbar, Typography, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

import { appURL } from '../Data/baseURL';
import NotificationMenu from './NotificationMenu';
import AccountMenu from './AccountMenu';
import CdpLogo from '../Data/Icons/CdpLogo';
import SelectLang from '../Controls/FormsUI/SelectLang';
import { appChromePalette, PublicContainer, publicPalette } from '../Public/PublicTheme';

const drawerWidth = 265;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
  }),
}));

const publicLinkSx = {
  px: 1.5,
  color: publicPalette.muted,
  textTransform: 'none',
  fontWeight: 700,
  fontSize: 14,
  '&:hover': {
    color: publicPalette.text,
    backgroundColor: alpha('#FFFFFF', 0.05),
  },
};

const Navbar = ({ open, setOpen, publicRoute }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { pathname } = useLocation();
  const lgMediaDown = useMediaQuery(theme.breakpoints.down('lg'));
  const smMediaDown = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAuthenticated, isAuthLoading } = useSelector((state) => state.authSlice);

  if (isAuthLoading) return null;

  const handleDrawerOpen = () => setOpen(true);

  const pageTitle = (() => {
    if (pathname === `${appURL}`) return t('dashboard.dashboard');
    if (pathname === `${appURL}/account`) return t('general.my_profile');
    if (pathname === `${appURL}/company`) return t('general.company_profile');
    if (pathname === `${appURL}/users`) return t('general.user_management');
    if (pathname === `${appURL}/subscription`) return t('general.subscription');
    if (pathname.startsWith(`${appURL}/quotes`)) return t(smMediaDown ? 'general.quotes_short' : 'general.quotes');
    if (pathname.startsWith(`${appURL}/invoices`)) return t('general.invs');
    if (pathname === `${appURL}/clients`) return t('general.clients');
    if (pathname === `${appURL}/bank-accounts`) return t('general.bank_accounts');
    if (pathname === `${appURL}/items-for-item-inv`) return t('general.invoice_items');
    if (pathname === `${appURL}/terms`) return t('general.invoice_terms');
    if (pathname === `${appURL}/terms-of-payment`) return t('general.terms_of_payment');
    if (pathname === `${appURL}/countries`) return t('general.countries');
    if (pathname === `${appURL}/currencies`) return t('general.currencies');
    if (pathname === `${appURL}/settings`) return t('settings.settings');
    return '';
  })();

  if (publicRoute) {
    return (
      <AppBar
        position="fixed"
        open={false}
        sx={{
          color: publicPalette.text,
          backgroundColor: alpha(publicPalette.background, 0.78),
          borderBottom: `1px solid ${publicPalette.border}`,
          boxShadow: 'none',
          backdropFilter: 'blur(22px)',
        }}
      >
        <PublicContainer>
          <Toolbar disableGutters sx={{ minHeight: { xs: 68, md: 76 }, gap: 2 }}>
            {lgMediaDown && (
              <IconButton
                edge="start"
                onClick={handleDrawerOpen}
                aria-label="Open navigation"
                sx={{ color: publicPalette.text }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Button component={NavLink} to="/" aria-label={t('general.main_page')} sx={{ p: 0, minWidth: 0 }}>
              <CdpLogo style={{ width: 112, maxWidth: '100%', height: 'auto', display: 'block' }} />
            </Button>

            {!lgMediaDown && (
              <Stack direction="row" spacing={0.25} sx={{ ml: 4 }}>
                <Button component="a" href="/#features" sx={publicLinkSx}>
                  {t('general.characteristics')}
                </Button>
                <Button component="a" href="/#workflow" sx={publicLinkSx}>
                  {t('base.title_howitworks')}
                </Button>
                <Button component="a" href="/#pricing" sx={publicLinkSx}>
                  {t('general.price')}
                </Button>
                <Button component={NavLink} to={`${appURL}/products`} sx={publicLinkSx}>
                  {t('general.products')}
                </Button>
                <Button component={NavLink} to={`${appURL}/faq`} sx={publicLinkSx}>
                  {t('faq.frequent_questions')}
                </Button>
              </Stack>
            )}

            <Box sx={{ flexGrow: 1 }} />
            <SelectLang />

            {!lgMediaDown &&
              (isAuthenticated ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    component={NavLink}
                    to={appURL}
                    startIcon={<DashboardIcon />}
                    sx={{
                      ml: 1,
                      px: 2.2,
                      color: publicPalette.text,
                      border: `1px solid ${publicPalette.border}`,
                      borderRadius: 2.5,
                      textTransform: 'none',
                      fontWeight: 800,
                    }}
                  >
                    {t('dashboard.dashboard')}
                  </Button>
                  <AccountMenu />
                </Stack>
              ) : (
                <>
                  <Button
                    component={NavLink}
                    to={`${appURL}/login`}
                    sx={{
                      ml: 1,
                      px: 2.2,
                      color: publicPalette.text,
                      border: `1px solid ${publicPalette.border}`,
                      borderRadius: 2.5,
                      textTransform: 'none',
                      fontWeight: 800,
                    }}
                  >
                    {t('general.login')}
                  </Button>
                  <Button
                    component={NavLink}
                    to={`${appURL}/signup`}
                    endIcon={<ArrowForwardRoundedIcon />}
                    sx={{
                      px: 2.4,
                      color: '#03111A',
                      background: `linear-gradient(110deg, ${publicPalette.primary}, ${publicPalette.accent})`,
                      borderRadius: 2.5,
                      textTransform: 'none',
                      fontWeight: 900,
                      boxShadow: `0 12px 30px ${alpha(publicPalette.primary, 0.2)}`,
                      '&:hover': { filter: 'brightness(1.08)' },
                    }}
                  >
                    {t('button.signup_for_free')}
                  </Button>
                </>
              ))}
          </Toolbar>
        </PublicContainer>
      </AppBar>
    );
  }

  return (
    <AppBar
      position="fixed"
      open={open}
      sx={{
        color: appChromePalette.text,
        background: appChromePalette.header,
        borderBottom: `1px solid ${appChromePalette.border}`,
        boxShadow: '0 10px 30px rgba(2, 6, 23, 0.16)',
        '& .MuiIconButton-root': {
          color: 'inherit',
        },
      }}
    >
      <Toolbar sx={{ minHeight: 56, px: { xs: 1.5, md: 3 } }}>
        {isAuthenticated && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="Open application menu"
            onClick={handleDrawerOpen}
            sx={{ mr: 2, ...(open && { display: 'none' }) }}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Typography variant="h6" color="inherit" noWrap sx={{ flexGrow: 1 }}>
          {pathname === '/' && (
            <Button component={NavLink} to={`${appURL}`} color="inherit" size="large">
              <DashboardIcon />
            </Button>
          )}
          {pageTitle}
        </Typography>

        {isAuthenticated && (
          <Stack direction="row" spacing={1}>
            <NotificationMenu />
            <AccountMenu />
          </Stack>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
